import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonDirective } from '@coreui/angular-pro';
import { MatomoTrackerService, TrackingErrorCategory, TrackingService, trackingErrorCategory } from 'analytics';
import { AuthService } from 'auth';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { LegalDocument, Logger, User } from 'shared';

import { CheckoutStartResponse, FunnelService } from '../../../services/funnel.service';
import {
  PLATINUM_PLAN_SLUG,
  PLATINUM_PRODUCT_CODE,
  SESSION_SPONSOR_KEY,
  consumeLoginReturn,
  readSiteReferral,
  rememberReferral,
  resolveSponsor,
  returnHereAfterLogin,
  validSponsorId
} from '../../../services/funnel-intent';
import { FunnelShellComponent } from '../funnel-shell/funnel-shell.component';

const MATOMO_CATEGORY = 'FAC3LESS Funnel';
/** Tracking Engine V1 page id of the app checkout (docs/tracking/EVENT_CONTRACT_V1.md §2). */
const TRACK_PAGE = 'f3-checkout';

/**
 * 'checkout'   — the purchase screen
 * 'existing'   — the e-mail belongs to an account: sign in / use the mailed link
 * 'forwarding' — signed in already, or the account was just created: on to the wizard
 */
type Mode = 'checkout' | 'existing' | 'forwarding';

/** Error codes of POST /funnel/checkout/start that have their own message. */
const KNOWN_ERRORS = ['name_required', 'email_invalid', 'captcha_required', 'captcha_failed',
                      'plan_invalid', 'not_available', 'consents_missing', 'consents_stale'];

/** Languages an account can be created in (fac3less = config domain 0, which has no
 *  `languages` list and therefore gets the generic superset). English is the default
 *  (customer decision 4) — never switched by location or browser. */
const ACCOUNT_LANGUAGES: { code: string; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
  { code: 'ru', label: 'Русский' },
  { code: 'cs', label: 'Čeština' },
  { code: 'sk', label: 'Slovenčina' },
  { code: 'ar', label: 'العربية' }
];

/**
 * FAC3LESS Platinum direct purchase (customer decisions 2026-10-09, no. 2 + 4):
 * a visitor from the landing page or a referral link buys the card WITHOUT a prior
 * registration, magic link or quiz.
 *
 *   card + full price ($444 = $359 here + $85 to the card partner) -> name, e-mail,
 *   language, legal consents, captcha -> POST /funnel/checkout/start -> order wizard
 *
 * The backend creates the account (and sets the soft-login cookie) and answers with
 * `next` — the complete wizard path, navigated to verbatim. An e-mail that already
 * has an account gets no session, only a sign-in link by mail.
 *
 * Served on /:lang/r and /:lang/r/:sponsorId (the Card Finder quiz is no longer on
 * the standard path). Ported from apps/globalhealth/.../funnel-direct-checkout-page,
 * without the GH plan/duration logic: there is exactly one product and no variant.
 */
@Component({
  selector: 'app-funnel-direct-checkout-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonDirective, TranslatePipe, FunnelShellComponent],
  templateUrl: './funnel-direct-checkout-page.component.html',
  styleUrls: ['./funnel-direct-checkout-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelDirectCheckoutPageComponent {
  private http       = inject(HttpClient);
  private router     = inject(Router);
  private route      = inject(ActivatedRoute);
  private funnel     = inject(FunnelService);
  private auth       = inject(AuthService);
  private i18n       = inject(I18nService);
  private translate  = inject(TranslateService);
  private matomo     = inject(MatomoTrackerService);
  private tracking   = inject(TrackingService);
  private destroyRef = inject(DestroyRef);
  /** form_interaction 'start' is sent once, on the first focus inside the form. */
  private formStarted = false;
  private logger     = new Logger('FunnelDirectCheckoutPage');

  private shell = viewChild(FunnelShellComponent);

  /** Customer-facing price split (decision 2): shown before the form, never only at the end. */
  readonly price = { total: 444, checkout: 359, activation: 85 };
  readonly languages = ACCOUNT_LANGUAGES;

  mode = signal<Mode>('forwarding');

  /** Newest referral (last touch). Shown as "referred by" and sent with the purchase. */
  sponsorId = signal<string | null>(null);

  // ----- Buyer + consents ---------------------------------------------------
  firstName = '';
  lastName  = '';
  email     = '';
  /** Account language (mails, documents). Defaults to the page language, else English. */
  language  = signal<string>('en');
  /** Re-evaluates the form-derived computeds (plain fields + ngModel). */
  private formTick = signal(0);
  onInput(): void { this.formTick.update(n => n + 1); }

  documents        = signal<LegalDocument[]>([]);
  documentsLoading = signal(false);
  documentsError   = signal(false);
  checked          = signal<Record<string, boolean>>({});
  /** Documents the backend reported as missing (422 consents_missing). */
  missing          = signal<string[]>([]);

  captchaToken   = signal<string | null>(null);
  captchaError   = signal(false);
  mcaptchaConfig = signal<{ widgetUrl: string; sitekey: string } | null>(null);

  loading = signal(false);
  error   = signal<string | null>(null);

  /** existing_account: whether the account already has a password. */
  hasPassword = signal(false);

  private static readonly EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  nameValid  = computed(() => { this.formTick(); return this.firstName.trim().length >= 1; });
  emailValid = computed(() => { this.formTick(); return FunnelDirectCheckoutPageComponent.EMAIL.test(this.email.trim()); });
  consentsComplete = computed(() =>
    this.documents().length > 0 &&
    this.documents().every(doc => !doc.required || this.checked()[doc.key])
  );
  canSubmit = computed(() =>
    this.nameValid() && this.emailValid() && this.consentsComplete() &&
    !!this.captchaToken() && !this.loading()
  );

  constructor() {
    const params = this.route.snapshot.paramMap;
    const query  = this.route.snapshot.queryParamMap;

    // Referral — last touch: a link in hand (path or ?ref) overwrites what this tab
    // and the browser remembered; without one, the newest stored referral applies.
    const explicit = validSponsorId(params.get('sponsorId')) ?? validSponsorId(query.get('ref'));
    let fromSession: string | null = null;
    try { fromSession = validSponsorId(sessionStorage.getItem(SESSION_SPONSOR_KEY)); } catch { /* ignore */ }
    const sponsor = resolveSponsor({
      path:    validSponsorId(params.get('sponsorId')),
      query:   validSponsorId(query.get('ref')),
      session: fromSession,
      site:    readSiteReferral()
    });
    this.sponsorId.set(sponsor);
    if (explicit) rememberReferral(explicit);
    else if (sponsor) { try { sessionStorage.setItem(SESSION_SPONSOR_KEY, sponsor); } catch { /* ignore */ } }

    const pageLang = this.lang();
    this.language.set(ACCOUNT_LANGUAGES.some(l => l.code === pageLang) ? pageLang : 'en');

    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'direct_checkout_entry', name: query.get('plan') ?? '' });
    this.enter();

    effect(() => { if (this.mode() === 'checkout' && !this.mcaptchaConfig()) this.loadMcaptchaConfig(); });
    effect(() => { if (this.mode() === 'checkout' && this.mcaptchaConfig()) setTimeout(() => this.loadMcaptchaWidget()); });

    const onMessage = this.handleMcaptchaMessage.bind(this);
    window.addEventListener('message', onMessage);
    this.destroyRef.onDestroy(() => window.removeEventListener('message', onMessage));
  }

  /** A signed-in visitor goes straight to the shop; everyone else buys here. */
  private enter(): void {
    // One product, one landing: the source is always the Platinum page.
    this.tracking.checkoutStarted(TRACK_PAGE, 'platinum', 'f3-platinum');
    this.auth.getUser$().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      if (user) {
        // Back from "sign in and come back to this purchase": restore the old home.
        consumeLoginReturn();
        this.mode.set('forwarding');
        this.forwardSignedIn();
        return;
      }
      this.mode.set('checkout');
      this.loadDocuments();
      this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'direct_checkout_view', name: PLATINUM_PLAN_SLUG });
      this.tracking.checkoutStep(TRACK_PAGE, 'account', 'view');
    });
  }

  /**
   * Signed in:
   *  - already entitled to the card (GET /mycards/summary -> card_partner.entitled,
   *    the same call the dashboard's My Cards link makes; a paid order creates the
   *    entitlement) -> My Cards, never a second purchase;
   *  - otherwise the order wizard with the Platinum Card in the cart, on the payment
   *    step (`?add=<productId>&step=2`). The product id is only known to the backend,
   *    so it is looked up by code in GET /products;
   *  - if either call fails or the card is not offered: the plain wizard.
   */
  private forwardSignedIn(): void {
    const toWizard = (productId: number | null) =>
      this.router.navigate(['/', this.lang(), 'order-wizard'], {
        queryParams: productId ? { add: productId, step: 2 } : {},
        replaceUrl: true
      });

    this.http.get<{ card_partner?: { entitled?: boolean } }>('/mycards/summary')
      .pipe(
        switchMap(summary => {
          if (summary?.card_partner?.entitled === true) return of({ entitled: true as const });
          return this.http.get<{ products: { id: number; code: string | null }[] }>('/products').pipe(
            map(res => ({
              entitled: false as const,
              productId: (res?.products ?? []).find(p => p.code === PLATINUM_PRODUCT_CODE)?.id ?? null
            }))
          );
        }),
        catchError(err => {
          this.logger.warn('entitlement/product lookup failed, opening the plain wizard', err);
          return of({ entitled: false as const, productId: null });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => {
        if (result.entitled) {
          this.router.navigate(['/', this.lang(), 'mycards'], { replaceUrl: true });
          return;
        }
        toWizard(result.productId);
      });
  }

  // ----- Language + legal documents -----------------------------------------

  setLanguage(code: string): void {
    if (!ACCOUNT_LANGUAGES.some(l => l.code === code) || code === this.language()) return;
    this.language.set(code);
    // Document versions are per language: the consents must match the language sent.
    this.loadDocuments();
  }

  loadDocuments(): void {
    this.documentsLoading.set(true);
    this.documentsError.set(false);
    this.funnel.getCheckoutLegal(this.language())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.documents.set(res.documents ?? []);
          // Never pre-ticked: every consent must be given actively.
          this.checked.set({});
          this.missing.set([]);
          this.documentsLoading.set(false);
        },
        error: err => {
          this.logger.error('failed to load checkout documents', err);
          this.documentsError.set(true);
          this.documentsLoading.set(false);
        }
      });
  }

  toggle(key: string, value: boolean): void {
    this.checked.update(current => ({ ...current, [key]: value }));
    if (value) this.missing.update(list => list.filter(k => k !== key));
  }

  openDocument(doc: LegalDocument, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.shell()?.openDocument(doc.url);
  }

  // ----- mCaptcha (same widget logic as the funnel landing page) ----------------

  private loadMcaptchaConfig(): void {
    this.http.get<{ widgetUrl: string; sitekey: string }>('/auth/mcaptcha_config')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: cfg => this.mcaptchaConfig.set(cfg),
        error: err => {
          this.logger.error('failed to load mcaptcha config', err);
          this.error.set(this.translate.translate('funnel.direct.errors.captcha_unavailable'));
        }
      });
  }

  private loadMcaptchaWidget(): void {
    const cfg = this.mcaptchaConfig();
    const container = document.getElementById('mcaptcha-widget');
    if (!cfg || !container) return;

    const iframe = document.createElement('iframe');
    iframe.src = `${cfg.widgetUrl}?sitekey=${cfg.sitekey}`;
    iframe.style.border = 'none';
    iframe.style.width = '100%';
    iframe.style.height = '95px';
    iframe.style.overflow = 'hidden';
    iframe.title = 'mCaptcha verification';
    iframe.setAttribute('scrolling', 'no');

    container.innerHTML = '';
    container.appendChild(iframe);
  }

  private handleMcaptchaMessage(event: MessageEvent): void {
    const widgetUrl = this.mcaptchaConfig()?.widgetUrl;
    if (!widgetUrl) return;
    let expectedOrigin: string;
    try { expectedOrigin = new URL(widgetUrl).origin; } catch { return; }
    if (event.origin !== expectedOrigin) return;

    const data = event.data;
    let token: string | null = null;
    if (typeof data === 'string')   token = data;
    else if (data?.token)           token = data.token;
    else if (data?.m_captcha_token) token = data.m_captcha_token;

    if (token) {
      this.captchaToken.set(token);
      this.captchaError.set(false);
    }
  }

  /** A captcha token is single-use: after any answer it has to be solved again. */
  private resetCaptcha(): void {
    this.captchaToken.set(null);
    this.loadMcaptchaWidget();
  }

  // ----- Submit ---------------------------------------------------------------

  /** First focus inside the account form (tracking only, no field values). */
  onFormFocus(): void {
    if (this.formStarted) return;
    this.formStarted = true;
    this.tracking.formInteraction(TRACK_PAGE, 'checkout', 'start');
  }

  submit(): void {
    this.onInput();
    if (!this.captchaToken()) this.captchaError.set(true);
    if (!this.canSubmit()) {
      if (!this.loading()) {
        this.tracking.formInteraction(TRACK_PAGE, 'checkout', 'error', this.captchaToken() ? 'validation' : 'captcha');
      }
      return;
    }
    this.tracking.formInteraction(TRACK_PAGE, 'checkout', 'submit');
    this.tracking.checkoutStep(TRACK_PAGE, 'account', 'submit');

    this.loading.set(true);
    this.error.set(null);
    this.missing.set([]);

    this.funnel.startCheckout({
      first_name:    this.firstName.trim(),
      last_name:     this.lastName.trim(),
      email:         this.email.trim(),
      language:      this.language(),
      sponsor_id:    this.sponsorId(),
      plan:          PLATINUM_PLAN_SLUG,
      weeks:         null,
      journey:       null,
      goal:          null,
      captcha_token: this.captchaToken(),
      consents:      this.documents()
                       .filter(doc => this.checked()[doc.key])
                       .map(doc => ({ key: doc.key, version: doc.version }))
    }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => this.onStarted(res),
        error: err => this.onFailed(err)
      });
  }

  private onStarted(res: CheckoutStartResponse): void {
    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'direct_checkout_submitted', name: res.status });
    if (res.status === 'existing_account') {
      this.tracking.formInteraction(TRACK_PAGE, 'checkout', 'error', 'exists');
      this.tracking.checkoutStep(TRACK_PAGE, 'account_exists', 'view');
      this.loading.set(false);
      this.hasPassword.set(!!res.has_password);
      this.mode.set('existing');
      this.scrollToTop();
      return;
    }

    this.tracking.formInteraction(TRACK_PAGE, 'checkout', 'complete');
    this.mode.set('forwarding');
    // `next` is the complete path incl. language — used verbatim.
    const next = res.next?.startsWith('/') ? res.next : `/${res.next ?? ''}`;
    this.adoptSession().subscribe({
      next: () => { this.loading.set(false); this.router.navigateByUrl(next, { replaceUrl: true }); },
      // The cookie is set but the client session could not be built: the login
      // page is the way in (the account mail carries a sign-in link too).
      error: () => { this.loading.set(false); this.router.navigate(['/', this.lang(), 'login'], { replaceUrl: true }); }
    });
  }

  private onFailed(err: any): void {
    this.loading.set(false);
    this.resetCaptcha();
    const code: string | undefined = err?.error?.code;
    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'direct_checkout_error', name: code ?? String(err?.status ?? '') });
    const category: TrackingErrorCategory = code?.startsWith('captcha') ? 'captcha'
      : code && KNOWN_ERRORS.includes(code) ? 'validation'
      : trackingErrorCategory(err);
    this.tracking.formInteraction(TRACK_PAGE, 'checkout', 'error', category);
    this.tracking.checkoutStep(TRACK_PAGE, 'account', 'error', category);

    if (err?.status === 409 && code === 'consents_stale') {
      // A document changed while the page was open: show the new version and ask again.
      this.error.set(this.translate.translate('funnel.direct.errors.consents_stale'));
      this.loadDocuments();
      return;
    }
    if (code === 'consents_missing') this.missing.set(err?.error?.missing ?? []);
    this.error.set(code && KNOWN_ERRORS.includes(code)
      ? this.translate.translate(`funnel.direct.errors.${code}`)
      : (err?.error?.error || this.translate.translate('funnel.direct.errors.generic')));
  }

  /** Turns the soft-login cookie into a client session (GET /user +
   *  AuthService.setAuthState), or authGuard bounces the wizard to /login. */
  private adoptSession(): Observable<unknown> {
    return this.http.get<{ user: User }>('/user').pipe(
      switchMap(res => from(this.auth.setAuthState(res.user, null)))
    );
  }

  // ----- Existing account -----------------------------------------------------

  /** Sign in, then come back here — which, signed in, forwards to the shop. */
  signIn(): void {
    returnHereAfterLogin(`/${this.lang()}/r?plan=${PLATINUM_PLAN_SLUG}`);
    this.router.navigate(['/', this.lang(), 'login']);
  }

  backToForm(): void {
    this.mode.set('checkout');
    this.loadDocuments();
  }

  private scrollToTop(): void {
    document.querySelector('.funnel-shell')?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  lang(): string {
    return this.i18n.getLanguage() || 'en';
  }
}
